import { useEffect, useRef, useState } from 'react';
import { UPDATE_CASE_ACTION } from '@demandproai/shared';
import { getDefaultDefendant } from '../utils/formSchema';
import { buildDefendantAddPayload, buildDefendantUpdatePayload } from '../utils/caseFieldPaths';
import useCaseFieldUpdate from './useCaseFieldUpdate';

const emptyEntry = () => ({ defendantId: '', name: '', insuranceName: '', claimNumber: '', adjusterName: '' });

const toEntry = (defendant) => ({
  defendantId: defendant.defendantId ?? '',
  name: defendant.personalInfo?.name ?? '',
  insuranceName: defendant.insurance?.insuranceName ?? '',
  claimNumber: defendant.insurance?.claimNumber ?? '',
  adjusterName: defendant.insurance?.adjuster?.name ?? ''
});

// a full defendant in the shape the case and the agent read, built from the lawyer's few fields
const buildDefendant = ({ entry, defendantId }) => {
  const base = getDefaultDefendant();

  return {
    ...base,
    defendantId: defendantId || base.defendantId,
    personalInfo: { ...base.personalInfo, name: entry.name.trim() },
    insurance: {
      ...base.insurance,
      insuranceName: entry.insuranceName.trim(),
      claimNumber: entry.claimNumber.trim(),
      adjuster: { ...base.insurance.adjuster, name: entry.adjusterName.trim() }
    }
  };
};

const useEasyModeDefendantStep = ({ caseData, defendantCount }) => {
  const { updateCase, updateCaseById } = useCaseFieldUpdate();
  const [isExpanded, setIsExpanded] = useState(false);
  // entriesRef mirrors entries synchronously so a persist that has just claimed an id is visible to a
  // concurrent one before React re-renders. Without it, a blur firing as Generate is clicked runs against
  // stale state and adds a second copy of the same defendant.
  const entriesRef = useRef([emptyEntry()]);
  const [entries, setEntriesState] = useState(entriesRef.current);
  // the case is created with one blank default defendant; the first named one reuses that slot instead of
  // leaving a nameless entry behind next to it. A ref so consuming it does not have to wait on a rerender.
  const reusableBlankIdRef = useRef(null);

  const setEntries = (updater) => {
    const next = typeof updater === 'function' ? updater(entriesRef.current) : updater;
    entriesRef.current = next;
    setEntriesState(next);
  };

  useEffect(() => {
    if (!caseData) return;

    const defendants = caseData.defendants ?? [];
    const named = defendants.filter((defendant) => defendant?.personalInfo?.name?.trim());

    reusableBlankIdRef.current = defendants.find((defendant) => !defendant?.personalInfo?.name?.trim())?.defendantId ?? null;
    setEntries(named.length ? named.map(toEntry) : [emptyEntry()]);
  }, [caseData]);

  // the cap is the count the lawyer set on Step 1; never below one so the panel always shows a form
  const maxDefendants = Math.max(Number(defendantCount) || 1, 1);
  const canAddMore = entries.length < maxDefendants;

  const toggleExpanded = () => setIsExpanded((wasExpanded) => !wasExpanded);

  const handleChange = ({ index, field, value }) =>
    setEntries((prev) => prev.map((entry, entryIndex) => (entryIndex === index ? { ...entry, [field]: value } : entry)));

  const addDefendant = () => setEntries((prev) => (prev.length < maxDefendants ? [...prev, emptyEntry()] : prev));

  const removeDefendant = async ({ index }) => {
    const entry = entriesRef.current[index];

    if (entry?.defendantId) {
      await updateCase({ data: { defendants: { defendantId: entry.defendantId } }, action: UPDATE_CASE_ACTION.REMOVE });
    }

    setEntries((prev) => (prev.length === 1 ? [emptyEntry()] : prev.filter((_, entryIndex) => entryIndex !== index)));
  };

  // persists the entry at index and returns the id it now lives under; a no-op for a still-empty name. The
  // id is written onto the entry before the network call, so a second caller for the same entry updates it
  // in place rather than adding a duplicate.
  const persistDefendant = async ({ index }) => {
    const entry = entriesRef.current[index];

    if (!entry?.name.trim()) return entry?.defendantId ?? '';

    if (entry.defendantId) {
      await updateCaseById(buildDefendantUpdatePayload([buildDefendant({ entry, defendantId: entry.defendantId })]));
      return entry.defendantId;
    }

    // claim the reusable blank slot, or a fresh id, atomically before yielding to the network
    const reusableId = reusableBlankIdRef.current;
    reusableBlankIdRef.current = null;
    const defendant = buildDefendant({ entry, defendantId: reusableId || '' });
    const { defendantId } = defendant;

    setEntries((prev) => prev.map((current, entryIndex) => (entryIndex === index ? { ...current, defendantId } : current)));

    if (reusableId) {
      await updateCaseById(buildDefendantUpdatePayload([defendant]));
    } else {
      await updateCase({ data: { defendants: buildDefendantAddPayload(defendant) }, action: UPDATE_CASE_ACTION.ADD });
    }

    return defendantId;
  };

  const handleBlur = ({ index }) => persistDefendant({ index });

  // called before the demand submits, so every named entry lands even if its field never blurred
  const save = async () => {
    for (let index = 0; index < entriesRef.current.length; index += 1) {
      await persistDefendant({ index });
    }
  };

  return {
    isExpanded,
    toggleExpanded,
    entries,
    maxDefendants,
    canAddMore,
    handleChange,
    handleBlur,
    addDefendant,
    removeDefendant,
    save
  };
};

export default useEasyModeDefendantStep;
